import { S } from '../state/store.js';
import { clearDocFonts, registerFace } from '../scene/fonts.js';
import { isSignedIn } from './auth.js';
import { canUpload } from './limits.js';
import { upload } from './assets.js';

/* Fonts the user brings with them.

   A font travels with its project, the same way a photo or a model does: kept as
   a data URL in a downloaded .json, and stored as an asset of kind 'font' when
   signed in. core/doc.js walks them alongside every other blob, so the v1→v2
   conversion, signing and stripping all work with no extra machinery.

   ⚠️ There is deliberately NO font-binary parsing here. woff2 tables are
   Brotli-compressed and DecompressionStream has no brotli, so reading the `name`
   table out of one would mean shipping a Brotli decoder to recover a string. The
   FontFace API lets the caller name the family outright, so the upload dialog
   prefills from the filename and the user can correct it. */

/** Strip the extension and tidy: "Acme-Bold_v2.woff2" → "Acme Bold v2". */
export function familyFromFilename(name) {
  return String(name || 'Custom')
    .replace(/\.(woff2?|ttf|otf|eot)$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || 'Custom';
}

/** A weight guessed from the filename, so the dialog opens on the likely answer. */
export function weightFromFilename(name) {
  const n = String(name || '').toLowerCase();
  if (/black|heavy|900/.test(n)) return 900;
  if (/extrabold|800/.test(n)) return 800;
  if (/bold|700/.test(n)) return 700;
  if (/semibold|600/.test(n)) return 600;
  if (/medium|500/.test(n)) return 500;
  if (/light|300/.test(n)) return 300;
  if (/thin|100/.test(n)) return 100;
  return 400;
}

const toDataUrl = blob => new Promise((res, rej) => {
  const rd = new FileReader();
  rd.onload = () => res(rd.result);
  rd.onerror = rej;
  rd.readAsDataURL(blob);
});

/**
 * Take a font file the user picked: register it so text can use it immediately,
 * and record it on the document so it survives a save.
 *
 * @param {File} file
 * @param {string} family   what to call it
 * @param {number} weight
 * @returns {Promise<{family:string, weight:number}>}
 */
export async function addFontFile(file, family, weight) {
  const buf = await file.arrayBuffer();

  /* Register FIRST. If the bytes are not a font this throws before anything has
     been written to the document or uploaded, so a bad file leaves no trace. */
  await registerFace(family, weight, buf.slice(0));

  const entry = { family, weight: +weight || 400 };

  if (isSignedIn()) {
    const allowed = await canUpload(file.size);
    if (!allowed.ok) throw new Error(allowed.reason);
    const asset = await upload(file, 'font');
    entry.assetId = asset.id;
    entry.src = null;
  } else {
    // Local-only: the font rides inside the downloaded .json, like an embedded photo.
    entry.src = await toDataUrl(file);
  }

  /* One entry per family+weight. Re-uploading the same weight replaces it, which
     is what lets someone correct a mistake without ending up with two. */
  S.fonts = (S.fonts || []).filter(f => !(f.family === family && f.weight === entry.weight));
  S.fonts.push(entry);
  return entry;
}

/** Forget a family entirely — every weight of it. */
export function removeFontFamily(family) {
  S.fonts = (S.fonts || []).filter(f => f.family !== family);
}

/**
 * Register every font a document carries, before its scene is applied.
 *
 * Order matters: applyDoc is synchronous and calls syncNode per object, so if
 * the faces are not in document.fonts by then the first trace of every text
 * object uses a fallback. The rebuild-on-ready path in buildText would correct
 * it a moment later, but doing it properly here means no visible flash of the
 * wrong letterforms.
 *
 * clearDocFonts runs first so a face from the previously open project cannot
 * shadow one of the same name in this document.
 *
 * @param {object} doc  already hydrated — `src` holds a data URL or a signed URL
 */
export async function registerDocFonts(doc) {
  clearDocFonts();
  const list = (doc && doc.fonts) || [];
  await Promise.all(list.map(async f => {
    if (!f.src) return;                  // an assetId that hydrate could not sign
    try {
      const buf = await (await fetch(f.src)).arrayBuffer();
      await registerFace(f.family, f.weight, buf);
    } catch (err) {
      console.warn('[illusio] could not register font', f.family, err);
    }
  }));
}
